import type { CurrencyCode } from '@monorepo/shared/schemas/common'
import {
  BILLING_PERIOD,
  type BillingPeriod,
  ENTITY_STATUS,
  type EntityStatus,
  TARGET_AUDIENCE,
  type TargetAudience,
} from '@monorepo/shared/schemas/common'
import type { CreatePlan, PlanResponse } from '@monorepo/shared/schemas/plans'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Save, X } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useCreatePlan, useUpdatePlan } from '../hooks/usePlans'

interface PlanDrawerProps {
  plan?: PlanResponse // undefined = CREATE mode, defined = EDIT mode
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Form type with required defaults
interface PlanFormData {
  name: string
  code: string
  description: string
  target_audience: TargetAudience
  currency: CurrencyCode
  billing_period: BillingPeriod
  trial_available: boolean
  trial_days: number
  status: EntityStatus
  metadata: Record<string, unknown>
}

export function PlanDrawer({ plan, open, onOpenChange }: PlanDrawerProps) {
  const navigate = useNavigate()
  const createPlanMutation = useCreatePlan()
  const updatePlanMutation = useUpdatePlan()

  const form = useForm<PlanFormData>({
    defaultValues: {
      name: '',
      code: '',
      description: '',
      target_audience: TARGET_AUDIENCE.both,
      currency: 'USD' as CurrencyCode,
      billing_period: BILLING_PERIOD.monthly,
      trial_available: false,
      trial_days: 0,
      status: ENTITY_STATUS.draft,
      metadata: {},
    },
  })

  // Update form data when plan changes or reset when creating
  useEffect(() => {
    if (plan && open) {
      // Edit mode: populate with plan data
      form.reset({
        name: plan.name,
        code: plan.code,
        description: plan.description || '',
        target_audience: plan.target_audience || TARGET_AUDIENCE.both,
        currency: plan.currency,
        billing_period: plan.billing_period || BILLING_PERIOD.monthly,
        trial_available: plan.trial_available || false,
        trial_days: plan.trial_days || 0,
        status: plan.status,
        metadata: plan.metadata || {},
      })
    } else if (!plan && open) {
      // Create mode: reset to default values
      form.reset({
        name: '',
        code: '',
        description: '',
        target_audience: TARGET_AUDIENCE.both,
        currency: 'USD',
        billing_period: BILLING_PERIOD.monthly,
        trial_available: false,
        trial_days: 0,
        status: ENTITY_STATUS.draft,
        metadata: {},
      })
    }
  }, [plan, open, form])

  const onSubmit = async (data: PlanFormData) => {
    try {
      // Convert form data to CreatePlan format
      const planData: CreatePlan = {
        name: data.name,
        code: data.code,
        description: data.description || undefined,
        target_audience: data.target_audience,
        currency: data.currency,
        billing_period: data.billing_period,
        trial_available: data.trial_available,
        trial_days: data.trial_days,
        status: data.status,
        metadata: data.metadata,
      }

      if (plan) {
        // Edit mode: update existing plan
        await updatePlanMutation.mutateAsync({
          id: plan.id,
          data: planData,
        })
        onOpenChange(false)
      } else {
        // Create mode: create new plan and navigate to detail
        const newPlan = await createPlanMutation.mutateAsync(planData)
        onOpenChange(false)
        navigate({
          to: '/catalog/plans/$planId',
          params: { planId: newPlan.id },
        })
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleCancel = () => {
    if (plan) {
      // Reset form to original values
      form.reset({
        name: plan.name,
        code: plan.code,
        description: plan.description || '',
        target_audience: plan.target_audience || TARGET_AUDIENCE.both,
        currency: plan.currency,
        billing_period: plan.billing_period || BILLING_PERIOD.monthly,
        trial_available: plan.trial_available || false,
        trial_days: plan.trial_days || 0,
        status: plan.status,
        metadata: plan.metadata || {},
      })
    }
    onOpenChange(false)
  }

  // Auto-generate code from name (only in create mode)
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value
    form.setValue('name', name)

    // Auto-generate code if not manually edited and in create mode
    if (!plan && !form.formState.dirtyFields.code) {
      const code = name.toLowerCase().replace(/\s+/g, '_')
      form.setValue('code', code)
    }
  }

  const isSaving = createPlanMutation.isPending || updatePlanMutation.isPending

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {plan ? 'Edit Plan Details' : 'Create New Plan'}
          </DrawerTitle>
          <DrawerDescription>
            {plan
              ? 'Update the plan information and settings.'
              : 'Configure the basic information for your pricing plan'}
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4 pb-6'>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
            {/* Plan Code (Read-only in edit mode) */}
            {plan && (
              <div>
                <Label htmlFor='code'>Plan Code</Label>
                <Input
                  id='code'
                  {...form.register('code')}
                  disabled
                  className='mt-1 bg-muted cursor-not-allowed'
                />
                <p className='text-xs text-slate-500 mt-1'>
                  Plan code cannot be changed after creation
                </p>
              </div>
            )}

            {/* Plan Name */}
            <div>
              <Label htmlFor='name'>Plan Name *</Label>
              <Input
                id='name'
                placeholder='e.g., Starter, Professional, Enterprise'
                {...form.register('name')}
                onChange={handleNameChange}
                className='mt-1'
              />
              {form.formState.errors.name && (
                <p className='text-sm text-destructive mt-1'>
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            {/* Code (only show in create mode since it's auto-generated) */}
            {!plan && (
              <div>
                <Label htmlFor='code-create'>Plan Code *</Label>
                <Input
                  id='code-create'
                  placeholder='e.g., starter_plan, professional_plan'
                  {...form.register('code')}
                  className='mt-1'
                />
                <p className='text-sm text-muted-foreground mt-1'>
                  Unique identifier for this plan (auto-generated from name)
                </p>
                {form.formState.errors.code && (
                  <p className='text-sm text-destructive mt-1'>
                    {form.formState.errors.code.message}
                  </p>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                placeholder='Describe this plan and its target audience...'
                rows={3}
                {...form.register('description')}
                className='mt-1'
              />
            </div>

            {/* Target Audience */}
            <div>
              <Label htmlFor='target_audience'>Target Audience *</Label>
              <Select
                value={form.watch('target_audience')}
                onValueChange={value =>
                  form.setValue('target_audience', value as TargetAudience)
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TARGET_AUDIENCE.human}>Humans</SelectItem>
                  <SelectItem value={TARGET_AUDIENCE.agent}>Agents</SelectItem>
                  <SelectItem value={TARGET_AUDIENCE.both}>Both</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              {/* Currency */}
              <div>
                <Label htmlFor='currency'>Currency *</Label>
                <Select
                  value={form.watch('currency')}
                  onValueChange={value =>
                    form.setValue('currency', value as CurrencyCode)
                  }
                >
                  <SelectTrigger className='mt-1'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='USD'>USD</SelectItem>
                    <SelectItem value='EUR'>EUR</SelectItem>
                    <SelectItem value='GBP'>GBP</SelectItem>
                    <SelectItem value='JPY'>JPY</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Billing Period */}
              <div>
                <Label htmlFor='billing_period'>Billing Period *</Label>
                <Select
                  value={form.watch('billing_period')}
                  onValueChange={value =>
                    form.setValue('billing_period', value as BillingPeriod)
                  }
                >
                  <SelectTrigger className='mt-1'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BILLING_PERIOD.daily}>Daily</SelectItem>
                    <SelectItem value={BILLING_PERIOD.weekly}>
                      Weekly
                    </SelectItem>
                    <SelectItem value={BILLING_PERIOD.monthly}>
                      Monthly
                    </SelectItem>
                    <SelectItem value={BILLING_PERIOD.quarterly}>
                      Quarterly
                    </SelectItem>
                    <SelectItem value={BILLING_PERIOD.semi_annual}>
                      Semi-Annual
                    </SelectItem>
                    <SelectItem value={BILLING_PERIOD.annual}>
                      Annual
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Trial Configuration */}
            <div className='space-y-4'>
              <div className='flex items-center gap-2'>
                <input
                  type='checkbox'
                  id='trial_available'
                  {...form.register('trial_available')}
                  className='rounded'
                />
                <Label htmlFor='trial_available' className='cursor-pointer'>
                  Enable trial period
                </Label>
              </div>

              {form.watch('trial_available') && (
                <div className='space-y-2 ml-6'>
                  <Label htmlFor='trial_days'>Trial Days</Label>
                  <Input
                    id='trial_days'
                    type='number'
                    min='0'
                    {...form.register('trial_days', { valueAsNumber: true })}
                    className='mt-1'
                  />
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <Label htmlFor='status'>Status *</Label>
              <Select
                value={form.watch('status')}
                onValueChange={value =>
                  form.setValue('status', value as EntityStatus)
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ENTITY_STATUS.draft}>Draft</SelectItem>
                  <SelectItem value={ENTITY_STATUS.active}>Active</SelectItem>
                  <SelectItem value={ENTITY_STATUS.inactive}>
                    Inactive
                  </SelectItem>
                  <SelectItem value={ENTITY_STATUS.testing}>Testing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Plan Info (Edit mode only) */}
            {plan && (
              <div className='pt-4 border-t'>
                <h4 className='font-medium text-sm text-slate-700 dark:text-slate-300 mb-3'>
                  Plan Information
                </h4>
                <div className='space-y-2 text-xs text-slate-500'>
                  <div>
                    <span className='font-medium'>ID:</span> {plan.id}
                  </div>
                  <div>
                    <span className='font-medium'>Created:</span>{' '}
                    {new Date(plan.created_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Updated:</span>{' '}
                    {new Date(plan.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        <DrawerFooter>
          <div className='flex gap-2 w-full'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                onClick={handleCancel}
                disabled={isSaving}
                className='flex-1'
              >
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={form.handleSubmit(onSubmit)}
              disabled={isSaving}
              className='flex-1'
            >
              {isSaving && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              <Save className='w-4 h-4 mr-2' />
              {plan ? 'Save Changes' : 'Create Plan'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
